import React, { useCallback, useEffect, useRef, useState } from "react";
import DashboardHeader from "../components/molecules/DashboardHeader";
import Pagination from "../components/molecules/Pagination";
import PhoneCallsTable from "../components/organisms/PhoneCallsTable";
import PhonePad from "../components/organisms/PhonePad";
import Softphone from "../components/organisms/Softphone";
import useSoftphone from "../hooks/useSoftphone";
import { ApiMeta } from "../services/HandleRequest";
import PhoneCallsService, {
  PhoneCall,
  PhoneCallsQuery,
} from "../services/PhoneCallsService";

const EMPTY_META: ApiMeta = {
  page: 1,
  per_page: 25,
  total: 0,
  total_pages: 0,
};

export default function PhoneCalls() {
  const softphone = useSoftphone();

  const [phonePadOpen, setPhonePadOpen] = useState<boolean>(true);
  const [calls, setCalls] = useState<PhoneCall[]>([]);
  const [meta, setMeta] = useState<ApiMeta>(EMPTY_META);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [direction, setDirection] = useState<PhoneCallsQuery["direction"]>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const requestId = useRef<number>(0);
  const phonePadToggleRef = useRef<HTMLButtonElement>(null);
  // During a call the panel owns its controls, so it cannot be hidden. Errors
  // only open it once: a rep must be able to read an error, then dismiss it.
  const phonePadInCall =
    softphone.status === "ringing" ||
    softphone.status === "dialing" ||
    softphone.status === "on-call";
  const phonePadVisible = phonePadOpen || phonePadInCall;
  const showPhonePadBadge = phonePadInCall || softphone.error !== "";
  let phonePadToggleLabel = phonePadVisible
    ? "Hide phone pad"
    : "Show phone pad";

  if (phonePadInCall) {
    phonePadToggleLabel = "Phone pad stays open during an active call";
  }

  const loadCalls = useCallback(async () => {
    // Filter changes fire overlapping requests; only the newest may render.
    requestId.current += 1;
    const thisRequest = requestId.current;
    const isStale = () => thisRequest !== requestId.current;

    setLoading(true);
    setError("");

    try {
      const response = await PhoneCallsService().list({
        page,
        search,
        direction,
      });

      if (isStale()) return;

      if (response.code !== 200) {
        setError(response.errors[0] || "The call list could not be loaded.");
        setCalls([]);
        setMeta(EMPTY_META);
        return;
      }

      setCalls((response.data as PhoneCall[]) || []);
      setMeta(response.meta || EMPTY_META);
    } catch {
      if (isStale()) return;

      setError("The call list could not be loaded.");
      setCalls([]);
      setMeta(EMPTY_META);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [direction, page, search]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  useEffect(() => {
    if (phonePadInCall || softphone.error) setPhonePadOpen(true);
  }, [phonePadInCall, softphone.error]);

  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const closePhonePad = () => {
    setPhonePadOpen(false);
    phonePadToggleRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <DashboardHeader title="Phone Calls" />

      <main className="max-w-7xl mx-auto py-6 px-4 pb-32 lg:px-8">
        <PhonePad
          id="phone-pad"
          isOpen={phonePadVisible}
          onClose={closePhonePad}
          closeDisabled={phonePadInCall}
        >
          <Softphone
            status={softphone.status}
            identity={softphone.identity}
            canPlaceCalls={softphone.canPlaceCalls}
            incomingFrom={softphone.incomingFrom}
            error={softphone.error}
            muted={softphone.muted}
            hasBottomMargin={false}
            startCall={softphone.startCall}
            acceptCall={softphone.acceptCall}
            rejectCall={softphone.rejectCall}
            hangUp={softphone.hangUp}
            toggleMute={softphone.toggleMute}
          />
        </PhonePad>

        <form
          className="flex flex-wrap items-end gap-3 mb-4"
          onSubmit={applyFilters}
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400" htmlFor="calls-search">
              Search
            </label>
            <input
              id="calls-search"
              type="search"
              value={searchInput}
              placeholder="Number or customer"
              onChange={(event) => setSearchInput(event.target.value)}
              className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-sm text-gray-100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400" htmlFor="calls-direction">
              Direction
            </label>
            <select
              id="calls-direction"
              value={direction}
              onChange={(event) => {
                setPage(1);
                setDirection(
                  event.target.value as PhoneCallsQuery["direction"],
                );
              }}
              className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-sm text-gray-100"
            >
              <option value="">All calls</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

          <button
            type="submit"
            className="px-3 py-1.5 rounded-md bg-indigo-600 text-sm text-white hover:bg-indigo-500"
          >
            Apply
          </button>
        </form>

        {error ? (
          <p className="mb-4 text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-xl border border-gray-700 p-4">
          <PhoneCallsTable
            calls={calls}
            loading={loading}
            onCall={
              softphone.status === "ready" && softphone.canPlaceCalls
                ? softphone.startCall
                : null
            }
          />

          <Pagination
            page={meta.page}
            totalPages={meta.total_pages}
            onPageChange={setPage}
          />
        </div>
      </main>

      <button
        ref={phonePadToggleRef}
        type="button"
        aria-label={phonePadToggleLabel}
        aria-controls="phone-pad"
        aria-expanded={phonePadVisible}
        disabled={phonePadInCall}
        onClick={() => setPhonePadOpen((isOpen) => !isOpen)}
        className="fixed bottom-3 right-3 z-10 flex h-16 w-16 items-center justify-center rounded-lg bg-black text-white shadow-2xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-75"
      >
        <svg
          aria-hidden="true"
          className="h-9 w-9"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.61 21 3 13.39 3 4c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2Z" />
        </svg>
        {showPhonePadBadge ? (
          <span
            aria-hidden="true"
            className="absolute right-1 top-1 h-4 w-4 animate-pulse rounded-full border-2 border-gray-900 bg-red-500"
          />
        ) : null}
      </button>
    </div>
  );
}
